import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@ludus/ui'
import { EmptyState } from './SourceDetailTab.tsx'

/**
 * The source's own page for this record, framed verbatim.
 *
 * Deliberately not a re-implementation. Where a source already renders the item
 * the way its own users see it — a 3D preview, a rendered document, a live
 * quote — rebuilding that here means porting its presentation code and keeping
 * two versions in step forever. The frame keeps exactly one implementation, on
 * the side that owns it.
 *
 * The URL is the adapter's whole answer (`LeadDetail.embedUrl`): the console
 * does not compose it, because how that page admits a viewer is the source's
 * business. The connection's stored credential is NEVER put on it — that is the
 * shared read access the media proxy exists to keep server-side, and a URL the
 * browser loads is exactly where it must not appear.
 *
 * ONE NARROW EXCEPTION, on this deploy line only: for an origin named in
 * `VITE_EMBED_KEY_ORIGINS`, the frame carries the VIEWER'S OWN Directus access
 * token as `?key=`. That is a different thing from the credential above.
 * Directus is this line's identity provider (`lib/api/auth.ts`) — the operator
 * signed in against it and the console holds *their* token, so handing it to a
 * source that also trusts Directus admits them as themselves and grants nothing
 * they did not already have. Without it tecraft's order page, which admits an
 * owner session or an active Directus user, shows every operator a 404 (that is
 * what it did from ludus 0.2.1-tecraft until this).
 *
 * The allowlist is the whole safety property, so it is an explicit origin match
 * and not a suffix test: an adapter is trusted to name a page, never to be
 * handed the viewer's identity for an origin nobody declared. `https:` is
 * required on top of it, because the allowlist is written by hand and a token
 * in a URL is only as private as the transport. The trunk sets the variable to
 * nothing and behaves exactly as before.
 *
 * Not sandboxed, because a source's own app generally needs scripts, workers
 * and canvas — a sandbox strong enough to matter would break the page it is
 * framing. `referrerPolicy="no-referrer"` keeps the console's URLs out of the
 * source's logs, and the frame mounts only when the tab is opened, so a heavy
 * page never loads for records nobody previews.
 */

interface SourceEmbedTabProps {
  url: string
}

export function SourceEmbedTab({ url }: SourceEmbedTabProps) {
  const { t } = useTranslation()
  const [loaded, setLoaded] = useState(false)
  // Read at render, not at module load: the operator may have signed in since
  // the bundle was evaluated, and a stale empty key would frame a 404 for the
  // whole session.
  const framed = useMemo(() => withViewerKey(url), [url])

  if (!isFramable(url)) {
    return (
      <EmptyState
        tone="error"
        text={t('pages.leads.detail.preview.unsafe', {
          defaultValue: 'The source offered a preview address that cannot be opened safely.',
        })}
      />
    )
  }

  return (
    <div className="relative h-[70vh] min-h-[480px] overflow-hidden rounded-lg border border-border/60 bg-background-secondary">
      {!loaded && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted">
          {t('pages.leads.detail.preview.loading', { defaultValue: 'Loading preview…' })}
        </div>
      )}
      <iframe
        src={framed}
        title={t('pages.leads.detail.preview.title', { defaultValue: 'Source preview' })}
        className={cn(
          'h-full w-full border-0 transition-opacity',
          loaded ? 'opacity-100' : 'opacity-0',
        )}
        referrerPolicy="no-referrer"
        onLoad={() => setLoaded(true)}
      />
    </div>
  )
}

/**
 * The adapter is trusted, but this string reaches an `iframe src` — so a
 * `javascript:` URL would execute in the console's own origin. Checking the
 * scheme costs nothing and closes the one way this contract could be turned
 * into script execution.
 */
function isFramable(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

/**
 * Origins that may be handed the viewer's own Directus token, comma-separated
 * (e.g. `https://tecraft.cz`). Compared as whole origins — scheme, host and
 * port — because that is the unit the browser itself isolates by. An `http://`
 * entry is inert: `withViewerKey` requires a confidential transport regardless
 * of what is listed here.
 */
const KEY_ORIGINS: readonly string[] = (import.meta.env.VITE_EMBED_KEY_ORIGINS ?? '')
  .split(',')
  .map((entry: string) => entry.trim())
  .filter((entry: string) => entry.length > 0)

/**
 * Add `?key=<the operator's Directus token>` for a declared `https:` origin, and
 * change nothing for any other. Returns the url untouched when the transport is
 * not confidential, when the origin is not listed, when there is no token, or
 * when the string will not parse — a frame that shows the source's own
 * not-found wall is the documented fallback, never an error the operator has to
 * decode.
 */
function withViewerKey(url: string): string {
  if (KEY_ORIGINS.length === 0) return url
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return url
  }
  // A bearer credential carried in a URL needs a confidential transport, and an
  // allowlist is written by hand: an entry someone typed as `http://…` would
  // otherwise put the operator's token on the wire in clear. Such a page still
  // frames — it is simply never keyed.
  if (parsed.protocol !== 'https:') return url
  if (!KEY_ORIGINS.includes(parsed.origin)) return url
  const token = readToken()
  if (token === null) return url
  // `set`, so an adapter that already put a key there wins nothing by it.
  parsed.searchParams.set('key', token)
  return parsed.toString()
}

/** The same bearer `apiClient` sends — see `lib/api/client.ts`. */
function readToken(): string | null {
  try {
    const token = localStorage.getItem('ludus.token')
    return token !== null && token.length > 0 ? token : null
  } catch {
    // Storage can throw outright where site data is blocked; a preview is not
    // worth taking the tab down for.
    return null
  }
}
